import React, { useState, useEffect, useRef } from 'react';
import { Grid, Button, MenuItem, InputLabel, Select, TextField, FormControl, useMediaQuery, Typography, IconButton, Menu, ListItemIcon } from '@mui/material';
import InventoryTable from './InventoryTable';
import { Loading } from '../../../components/loading/Loading';
import AddInventoryModal from './modals/AddInventoryModal';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { Add, FilterAlt, MoreVert, QrCode, Close } from '@mui/icons-material';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { useInventoryContext } from '../../../context/inventory_context/InventoryContext';
import { TablePaginationKeys } from '../../../components/pagination/TablePaginationKeys';
import { handleChangePage, handleChangeRowsPerPage } from '../../../components/pagination/PaginationHelpers';
import FilterDialog from '../../../components/filter_dialog/FilterDialog';
import { QRCodeSVG } from 'qrcode.react';
import ReactDOM from 'react-dom/client';
import { handleMenuClick, handleMenuClose } from '../../../components/utils/MenuHandlers';

function Inventory() {
    const [showAddInventoryModal, setShowAddInventoryModal] = useState(false);
    const [supplierFilter, setSupplierFilter] = useState('all');
    const [locationFilter, setLocationFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const { inventoryLocations, suppliers, inventoryItems } = useInventoryContext();
    const isMobile = useMediaQuery('(max-width:600px)');
    const { hasPermission } = usePermissions();
    const { t } = useTranslation();
    const qrRef = useRef();

    useEffect(() => {
        const savedRowsPerPage = localStorage.getItem(TablePaginationKeys.InventoryTableRowsPerPage);
        if (savedRowsPerPage) {
            setRowsPerPage(parseInt(savedRowsPerPage, 10));
        }
    }, []);

    const handleSelectionChange = (selectedIds) => {
        setSelectedItems(selectedIds);
    };

    const handleExitSelectionMode = () => {
        setIsSelectionMode(false);
        setSelectedItems([]);
    };

    const handlePrintSelectedQRCodes = () => {
        const selectedItemsData = inventoryItems.filter(item => selectedItems.includes(item.id));
        
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`<html><head><title>${t(StringKeys.PRINT_QR_CODE)}</title>`);
        printWindow.document.write('<style>');
        printWindow.document.write(`
            @media print { 
                body {
                    margin: 0;
                    padding: 0;
                }
                .print-only { 
                    display: block;
                }
                .page-container {
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    align-content: center;
                    padding: 0.25in;
                    box-sizing: border-box;
                    min-height: 8.5in;
                }
                .qr-container { 
                    display: inline-block;
                    margin: 0.1in;
                    padding: 0.1in;
                    border: 1px solid #ccc;
                    text-align: center;
                    width: 2.5in;
                    height: 3.8in;
                    vertical-align: top;
                    box-sizing: border-box;
                }
                .image-container {
                    width: 0.8in;
                    height: 0.8in;
                    margin: 0.1in auto;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .image-container img {
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                }
                .page-break { 
                    page-break-after: always; 
                    break-after: page;
                    clear: both;
                }
                @page {
                    size: letter landscape;
                    margin: 0;
                }
                h4 {
                    margin: 0.1in 0;
                    min-height: 0.3in;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12pt;
                }
                p {
                    margin: 0.05in 0;
                    font-size: 10pt;
                }
                .qr-code-container {
                    margin: 0.1in 0;
                }
                .qr-code-container svg {
                    width: 1.2in;
                    height: 1.2in;
                }
            }
        `);
        printWindow.document.write('</style></head><body><div class="page-container">');
        
        selectedItemsData.forEach((item, index) => {
            const itemUrl = `${window.location.origin}/inventory/${item.id}`;
            printWindow.document.write(`
                <div class="qr-container">
                    <h4>${item.name}</h4>
                    <p>${t(StringKeys.MINIMUM_QUANTITY)}: ${item.minimum_quantity}</p>
                    <p>${t(StringKeys.REORDER_QUANTITY)}: ${item.reorder_quantity}</p>
                    <div class="image-container">
                        ${item.image_url 
                            ? `<img src="${item.image_url}" alt="Item Image" />` 
                            : `<svg width="100%" height="100%" viewBox="0 0 100 100">
                                <rect width="100" height="100" fill="#f0f0f0"/>
                                <text x="50" y="50" font-family="Arial" font-size="12" fill="#666" text-anchor="middle" dominant-baseline="middle">No Image</text>
                               </svg>`
                        }
                    </div>
                    <div class="qr-code-container" id="qr-code-${item.id}"></div>
                </div>
                ${(index + 1) % 8 === 0 ? '</div><div class="page-break"></div><div class="page-container">' : ''}
            `);
        });
        
        printWindow.document.write('</div></body></html>');
        printWindow.document.close();

        // Create a temporary container in the current document
        const tempContainer = document.createElement('div');
        tempContainer.style.display = 'none';
        document.body.appendChild(tempContainer);

        // Render QR codes in the temporary container
        selectedItemsData.forEach(item => {
            const itemUrl = `${window.location.origin}/inventory/${item.id}`;
            const qrContainer = printWindow.document.getElementById(`qr-code-${item.id}`);
            
            // Clear any existing content
            qrContainer.innerHTML = '';
            
            // Create a new container for the QR code
            const qrCodeContainer = document.createElement('div');
            qrCodeContainer.id = `temp-qr-${item.id}`;
            tempContainer.appendChild(qrCodeContainer);

            // Render the QR code using ReactDOM
            const root = ReactDOM.createRoot(qrCodeContainer);
            root.render(<QRCodeSVG value={itemUrl} size={128} />);

            // Wait for React to finish rendering and copy the SVG to the print window
            setTimeout(() => {
                const svg = qrCodeContainer.querySelector('svg');
                if (svg) {
                    qrContainer.appendChild(svg.cloneNode(true));
                }
            }, 0);
        });

        // Wait a bit for all QR codes to be rendered, then print and cleanup
        setTimeout(() => {
            printWindow.print();
            document.body.removeChild(tempContainer);
            // Turn off selection mode and clear selected items
            setIsSelectionMode(false);
            setSelectedItems([]);
        }, 500);
    };

    const getFilteredInventory = () => {
        return inventoryItems
            .filter(item => {
                const supplierMatch = supplierFilter === 'all' || item.supplier === supplierFilter;
                const locationMatch = locationFilter === 'all' || item.location === locationFilter;
                const searchMatch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
                return supplierMatch && locationMatch && searchMatch;
            })
            .sort((a, b) => {
                return sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
            });
    };

    const sortedSuppliers = [...suppliers].sort((a, b) => a.name.localeCompare(b.name));
    const sortedLocations = [...inventoryLocations].sort((a, b) => a.name.localeCompare(b.name));

    const handleOpenBottomSheet = () => {
        setIsBottomSheetOpen(true);
    };

    const handleCloseBottomSheet = () => {
        setIsBottomSheetOpen(false);
    };

    return (
        <>
            {inventoryItems ? (
                <AppSidebar
                    content={
                        <>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12}>
                                    <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                        {!isMobile && (
                                            <Grid item>
                                                <Typography variant="h5">{t(StringKeys.INVENTORY)}</Typography>
                                            </Grid>
                                        )}
                                        <Grid item>
                                            <Grid container spacing={2} alignItems="center">
                                                {!isMobile && (
                                                    <>
                                                        <Grid item>
                                                            <TextField
                                                                label={t(StringKeys.SEARCH)}
                                                                variant="outlined"
                                                                size="small"
                                                                value={searchTerm}
                                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                                                                <InputLabel>{t(StringKeys.SORT)}</InputLabel>
                                                                <Select
                                                                    value={sortOrder}
                                                                    onChange={(e) => setSortOrder(e.target.value)}
                                                                    label={t(StringKeys.SORT)}
                                                                >
                                                                    <MenuItem value="asc">A-Z</MenuItem>
                                                                    <MenuItem value="desc">Z-A</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                        <Grid item>
                                                            <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                                                                <InputLabel>{t(StringKeys.SUPPLIER)}</InputLabel>
                                                                <Select
                                                                    value={supplierFilter}
                                                                    onChange={(e) => setSupplierFilter(e.target.value)}
                                                                    label={t(StringKeys.SUPPLIER)}
                                                                >
                                                                    <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                                                    {sortedSuppliers.map(supplier => (
                                                                        <MenuItem key={supplier.name} value={supplier.name}>{supplier.name}</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                        <Grid item>
                                                            <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                                                                <InputLabel>{t(StringKeys.LOCATION)}</InputLabel>
                                                                <Select
                                                                    value={locationFilter}
                                                                    onChange={(e) => setLocationFilter(e.target.value)}
                                                                    label={t(StringKeys.LOCATION)}
                                                                >
                                                                    <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                                                    {sortedLocations.map(location => (
                                                                        <MenuItem key={location.name} value={location.name}>{location.name}</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                        {!isSelectionMode ? (
                                                            <>
                                                                <Grid item>
                                                                    <Button
                                                                        variant="contained"
                                                                        onClick={() => setIsSelectionMode(true)}
                                                                        startIcon={<QrCode />}
                                                                    >
                                                                        {t(StringKeys.PRINT_QR_CODES)}
                                                                    </Button>
                                                                </Grid>
                                                                {hasPermission(Modules.INVENTORY, PermissionTypes.WRITE) && (
                                                                    <Grid item>
                                                                        <Button
                                                                            variant="contained"
                                                                            onClick={() => setShowAddInventoryModal(true)}
                                                                            startIcon={<Add />}
                                                                        >
                                                                            {t(StringKeys.ADD_ITEM)}
                                                                        </Button>
                                                                    </Grid>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Grid item>
                                                                    <Button
                                                                        variant="contained"
                                                                        onClick={handleExitSelectionMode}
                                                                    >
                                                                        {t(StringKeys.CANCEL)}
                                                                    </Button>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Button
                                                                        variant="contained"
                                                                        onClick={handlePrintSelectedQRCodes}
                                                                        startIcon={<QrCode />}
                                                                        disabled={selectedItems.length === 0}
                                                                    >
                                                                        {selectedItems.length > 0 
                                                                            ? t(StringKeys.PRINT_X_SELECTED_QR_CODES, { count: selectedItems.length })
                                                                            : t(StringKeys.SELECT_ITEMS_TO_PRINT)
                                                                        }
                                                                    </Button>
                                                                </Grid>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {isMobile && (
                                        <>
                                            <Typography variant="h6">{t(StringKeys.INVENTORY)}</Typography>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={10}>
                                                    <TextField
                                                        label={t(StringKeys.SEARCH)}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                                                        <MoreVert />
                                                    </IconButton>
                                                    <Menu
                                                        anchorEl={anchorEl}
                                                        open={Boolean(anchorEl)}
                                                        onClose={() => handleMenuClose(setAnchorEl)}
                                                    >
                                                        <MenuItem onClick={() => { handleOpenBottomSheet(); handleMenuClose(setAnchorEl); }}>
                                                            <ListItemIcon>
                                                                <FilterAlt />
                                                            </ListItemIcon>
                                                            {t(StringKeys.FILTER)}
                                                        </MenuItem>
                                                        <MenuItem onClick={() => { setShowAddInventoryModal(true); handleMenuClose(setAnchorEl); }}>
                                                            <ListItemIcon>
                                                                <Add />
                                                            </ListItemIcon>
                                                            {t(StringKeys.ADD_ITEM)}
                                                        </MenuItem>
                                                    </Menu>
                                                </Grid>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>

                                {/* Inventory Table */}
                                <Grid item xs={12}>
                                    <InventoryTable
                                        inventory={getFilteredInventory()}
                                        page={page}
                                        rowsPerPage={rowsPerPage}
                                        handleChangePage={(event, newPage) => handleChangePage(event, newPage, setPage)}
                                        handleChangeRowsPerPage={(event) => handleChangeRowsPerPage(event, TablePaginationKeys.InventoryTableRowsPerPage, setPage, setRowsPerPage)}
                                        onSelectionChange={handleSelectionChange}
                                        isSelectionMode={isSelectionMode}
                                    />
                                </Grid>

                                {/* Add Inventory Modal */}
                                <AddInventoryModal
                                    show={showAddInventoryModal}
                                    onHide={() => setShowAddInventoryModal(false)}
                                />
                            </Grid>
                            <FilterDialog
                                open={isBottomSheetOpen}
                                onClose={handleCloseBottomSheet}
                                sortOrder={sortOrder}
                                setSortOrder={setSortOrder}
                                filters={[
                                    <Grid item>
                                        <FormControl variant="outlined" fullWidth size="small" style={{ minWidth: 130 }}>
                                            <InputLabel>{t(StringKeys.SUPPLIER)}</InputLabel>
                                            <Select
                                                value={supplierFilter}
                                                onChange={(e) => setSupplierFilter(e.target.value)}
                                                label={t(StringKeys.SUPPLIER)}
                                            >
                                                <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                                {sortedSuppliers.map(supplier => (
                                                    <MenuItem key={supplier.name} value={supplier.name}>{supplier.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>,
                                    <Grid item>
                                        <FormControl variant="outlined" fullWidth size="small" style={{ minWidth: 130 }}>
                                            <InputLabel>{t(StringKeys.LOCATION)}</InputLabel>
                                            <Select
                                                value={locationFilter}
                                                onChange={(e) => setLocationFilter(e.target.value)}
                                                label={t(StringKeys.LOCATION)}
                                            >
                                                <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                                {sortedLocations.map(location => (
                                                    <MenuItem key={location.name} value={location.name}>{location.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ]}
                            />
                        </>
                    }
                />
            ) : (
                <AppSidebar content={<Loading />} />
            )}
        </>
    );
}

export default Inventory;
